/**
 *  @swagger
 *  definitions:
 *    AdminParams:
 *      type: object
 *      properties:
 *        Name:
 *          description: Name of the Admin
 *          type: string
 *        Email:
 *          description: Email of the Admin
 *          type: string
 *          format: email
 *        Phone:
 *          description: Phone of the Admin
 *          type: string
 *          nullable: true
 *        Password:
 *          description: Password of the Admin
 *          type: string
 *          nullable: true
 *        Status:
 *          description: Status of the Admin
 *          $ref: '#/components/enums/AdminStatus'
 *        RemoteImage:
 *          description: Remote Image of the Admin
 *          type: string
 *          nullable: true
 *        Super:
 *          description: Super Admin
 *          type: boolean
 *        Review:
 *          description: Review Admin
 *          type: boolean
 *        InvoiceName:
 *          description: Invoice Name of the Admin
 *          type: string
 *          nullable: true
 *        InvoiceNo:
 *          description: Invoice No of the Admin
 *          type: string
 *          nullable: true
 *        InvoiceVat:
 *          description: Invoice Vat of the Admin
 *          type: string
 *          nullable: true
 *        InvoiceAddress:
 *          description: Invoice Address of the Admin
 *          type: string
 *          nullable: true
 *
 *  @swagger
 *  definitions:
 *    AdminCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/AdminParams'
 *        - type: object
 *          required:
 *            - Name
 *            - Email
 *            - Status
 *            - Review
 *
 *  @swagger
 *  definitions:
 *    AdminQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the Admin
 *              type: string
 *        - $ref: '#/definitions/AdminParams'
 *
 *  @swagger
 *  definitions:
 *    AdminUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/AdminQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    Admin:
 *      allOf:
 *        - $ref: '#/definitions/AdminCreateParams'
 *        - $ref: '#/definitions/AdminUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      Admin:
 *        allOf:
 *          - $ref: '#/definitions/Admin'
 *          - type: object
 *            properties:
 *              AdminSocialUsers:
 *                description: AdminSocialUsers of the Admin
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/AdminSocialUser'
 *              AdminAccessTokens:
 *                description: AdminAccessTokens of the Admin
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/AdminAccessToken'
 *              Regattas:
 *                description: Regattas of the Admin
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/Regatta'
 *              Championships:
 *                description: Championships of the Admin
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/Championship'
 */

// Enums
import {
  AdminStatus,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $Params = {
  Email: string;
  InvoiceAddress: null | string;
  InvoiceName: null | string;
  InvoiceNo: null | string;
  InvoiceVat: null | string;
  Name: string;
  Password: null | string;
  Phone: null | string;
  RemoteImage: null | string;
  Review: boolean;
  Status: AdminStatus;
  Super: boolean;
};

export type $CreateParams = $Params;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$Params>;

export type $Associations = {
  AdminAccessTokens?: Array<$Model<'AdminAccessToken'>>;
  AdminSocialUsers?: Array<$Model<'AdminSocialUser'>>;
  Championships?: Array<$Model<'Championship'>>;
  Regattas?: Array<$Model<'Regatta'>>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Email: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      isEmail: true,
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  InvoiceAddress: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  InvoiceName: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  InvoiceNo: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  InvoiceVat: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Password: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Phone: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  RemoteImage: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Review: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  Status: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(AdminStatus)),
    validate: {
      notEmpty: true,
    },
  },
  Super: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'Admin'> => {
  const Admin = stack.define(
    'Admin',
    fields(Sequelize),
    {
      tableName: 'Admins',
    },
  );

  Admin.associate = (models: $ModelClasses) => {
    models.Admin.hasMany(
      models.AdminSocialUser,
      {
        as: 'AdminSocialUsers',
        foreignKey: 'AdminId',
      },
    );
    models.Admin.hasMany(
      models.AdminAccessToken,
      {
        as: 'AdminAccessTokens',
        foreignKey: 'AdminId',
      },
    );
    models.Admin.belongsToMany(
      models.Regatta,
      {
        as: 'Regattas',
        foreignKey: 'AdminId',
        otherKey: 'RegattaId',
        through: models.RegattaAdmin,
      },
    );
    models.Admin.belongsToMany(
      models.Championship,
      {
        as: 'Championships',
        foreignKey: 'AdminId',
        otherKey: 'ChampionshipId',
        through: models.ChampionshipAdmin,
      },
    );
  };

  Admin.addScope(
    'defaultScope',
    {
      order: [['Name', 'ASC']],
    },
  );

  return Admin;
});
